import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '@anytag/shared-utils';
import { useUserRoles } from '@anytag/auth';
import { ButtonSubmit, Form } from '@anytag/shared-atoms';
import { useAllAvailableCountryOptions } from '@anytag/shared-hooks';
import {
  TextField,
  SelectorField,
  PasswordField,
  CheckBoxField,
  AsyncSearchAdvertiserField,
  SingleAsyncSearchAdvertiserFieldProps,
  AsyncSearchHubspotCompanyField,
  SingleAsyncSearchHubspotCompanyFieldProps
} from '@anytag/shared-molecules';
import { AdvertiserFormKeys, AdvertiserFormValues } from './types';

export const Template = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { allAvailableCountryOptions } = useAllAvailableCountryOptions();
  const {
    watch,
    reset,
    setValue,
    formState: { isSubmitting }
  } = useFormContext<AdvertiserFormValues>();

  const values = watch();
  const isAddMode = values?.type === 'add';
  const canSelectNetSuite = isAdminStaff;
  const isRequiredUserFields =
    isAddMode && !!(values.userName || values.email || values.password || values.passwordConfirm);
  const isDisabledForExistingAdvertiser = isAddMode && values.isNewlyCreated === false;

  const handleClickSendEmail = (_: string, value: boolean) => {
    if (isAddMode) {
      // we need to clear password fields each time user click send email to prevent validation err on password
      reset({ ...values, password: '', passwordConfirm: '', shouldSendInvitation: value });
    }
  };

  const handleHubSpotChange: SingleAsyncSearchHubspotCompanyFieldProps<AdvertiserFormKeys>['onChange'] = (
    name,
    value,
    options
  ) => {
    const option = options?.find((option) => option.value === value);

    setValue(name, value, { shouldValidate: true });
    setValue('advertiserName', option?.label || '');
    setValue('internalNetsuiteId', option?.internalNetsuiteId || '');
  };

  const handleAdvertiserChange: SingleAsyncSearchAdvertiserFieldProps<AdvertiserFormKeys>['onChange'] = (
    name,
    value,
    options
  ) => {
    const option = options?.find((option) => option.value === value);

    setValue(name, value, { shouldValidate: true });
    setValue('companyUrl', option?.companyUrl || '');
    setValue('phoneNumber', option?.phoneNumber || '');
    setValue('isNewlyCreated', option ? option._isNew ?? false : undefined);
    setValue('advertiserName', option?.label || '', { shouldValidate: true });
    setValue('countryId', option?.country?.id || '', { shouldValidate: true });
    setValue('hubspotId', option?._isNew ? null : option?.hubspotId || ''); // setting `null` means that we want to clear hubspot selector
  };

  return (
    <>
      <Form.Body className={cn({ 'rounded-none': !isAddMode }, className)}>
        <Form.SectionTitle index={isAddMode ? 1 : undefined} label={t('Company Information')} />

        {canSelectNetSuite ? (
          <AsyncSearchHubspotCompanyField<AdvertiserFormKeys>
            name="hubspotId"
            onChange={handleHubSpotChange}
            disabled={isDisabledForExistingAdvertiser}
          />
        ) : null}

        {isAddMode ? (
          <AsyncSearchAdvertiserField<AdvertiserFormKeys>
            name="advertiserId"
            onChange={handleAdvertiserChange}
            title={t('Selector.Advertiser Name')}
            help={t<string>('Tooltip.Advertiser Name')}
            placeholder={t<string>('TextForm.AdAsiaLtd')}
            required
            creatable
          />
        ) : (
          <TextField<keyof AdvertiserFormValues>
            name="advertiserName"
            help={'Advertiser Name'}
            title={t('TextForm.Advertiser Name')}
            placeholder={t<string>('TextForm.AdAsiaLtd')}
            required
          />
        )}

        <TextField<AdvertiserFormKeys>
          name="companyUrl"
          help={t('Tooltip.Company URL')}
          title={t('TextForm.Company URL')}
          placeholder="https://anymindgroup.com"
          disabled={isDisabledForExistingAdvertiser}
        />

        <Form.Fields itemsPerRow={2}>
          <TextField<AdvertiserFormKeys>
            name="phoneNumber"
            help={t('Tooltip.Phone Number')}
            placeholder="+66020485707"
            title={t('TextForm.Phone Number')}
            disabled={isDisabledForExistingAdvertiser}
          />
          <SelectorField<AdvertiserFormKeys>
            title={t('Selector.Country')}
            name="countryId"
            help={t('Tooltip.Advertiser Country')}
            required
            options={allAvailableCountryOptions}
            disabled={isDisabledForExistingAdvertiser}
          />
        </Form.Fields>

        {isAddMode ? (
          <>
            <Form.SectionTitle index={2} label={t('User Information')} className="mt-6" />

            <TextField<AdvertiserFormKeys>
              name="userName"
              title={t('TextForm.Name')}
              required={isRequiredUserFields}
              placeholder={t<string>('NameExample')}
            />

            <Form.Fields itemsPerRow={2}>
              <TextField<AdvertiserFormKeys>
                name="email"
                placeholder="your@email.com"
                required={isRequiredUserFields}
                title={t('TextForm.Email Address')}
              />
            </Form.Fields>

            {!values.shouldSendInvitation && (
              <Form.Fields itemsPerRow={2}>
                <PasswordField<AdvertiserFormKeys>
                  name="password"
                  title={t('Password.Password')}
                  required={isRequiredUserFields}
                  dynamicError
                />

                <PasswordField<AdvertiserFormKeys>
                  name="passwordConfirm"
                  required={isRequiredUserFields}
                  title={t('Password.Confirm Password')}
                />
              </Form.Fields>
            )}

            <CheckBoxField<AdvertiserFormKeys>
              name="shouldSendInvitation"
              label={t('Send invitation email')}
              onChange={handleClickSendEmail}
            />
          </>
        ) : null}
      </Form.Body>

      <Form.Footer>
        <ButtonSubmit type="submit" title={t('Button.Save')} loading={isSubmitting} />
      </Form.Footer>
    </>
  );
};
